import { isBoolean } from 'lodash';

import { ProblemData } from '@gmm/problem';

import { postOverride } from '..';
import {
  ReceiveProblem,
  receiveProblem,
  unlockAll,
} from '../../api/responseHandlerShared';
import {
  bannerState,
  clearAllPenalties,
  problemState,
  setProblems,
} from '../../stores';
import { getGmm, getIsTest } from '../../stores/globalState';
import { applyPracticeLockChangedMessage } from '../../stores/practiceLockStore';
import { studentAppModalState } from '../../stores/studentAppModalStore';
import { workState } from '../../stores/workStore';
import { Any, Exam, ID } from '../../types';
import { gmmAlert, allExamProblemsAreLocked } from '../../utils';
import {
  logHistory,
  loginInternal,
  reboot,
  submitTest,
} from '../../utils/gmmUtils';
import { alerts } from '../alerts';
import { changeHandStatus } from '../sendRaisedHand/util';

import { PingSuccess } from './types';

interface ExamExtend {
  autoUnlockExamsProblems?: boolean;
  problems?: 'empty';
}

export interface MessageTypes {
  accountDeleted: boolean;
  bg: boolean;
  forcelogoff: boolean;
  getHelp: boolean;
  hand: boolean;
  id: ID;
  msg: string;
  override: boolean;
  p: ProblemData;
  permitReadAloud: boolean;
  reason: string;
  replacementsPerDay: number | undefined;
  sender: string;
  sendErrorToServer: boolean;
  sitId: number;
  tchrmsg: string;
  test: ExamExtend | Any;
  testId: number;
  testRequestResponse: boolean;
  testRequestUnlockResponse: boolean;
  manualPracticeLocked?: boolean;
  messageType?: 'practiceLockChanged';
  type?:
    | 'clearPenalties'
    | 'denyunlock'
    | 'limitMultipleChoice'
    | 'practiceLockChanged'
    | 'targetedOverride'
    | 'teacherTurnedInExam'
    | 'teacherPausedExam'
    | 'unlock';
}

export const teacherToStudentMessages = (data: PingSuccess): void => {
  data.m.forEach((msg: MessageTypes) => {
    if (msg.testRequestResponse) {
      studentAppModalState().setExamLoading(false);
    }

    if (msg.testRequestUnlockResponse) {
      if (getIsTest() && msg.msg) {
        gmmAlert({
          msg: msg.msg,
          top: 'Message',
        });
      }
    }

    if (msg.accountDeleted) {
      reboot('You have been removed from this class.');

      return;
    }

    if (msg.forcelogoff) {
      gmmAlert(alerts.loggedOff(msg.reason));
    }

    if (isBoolean(msg.bg)) {
      bannerState().setBlockGames(msg.bg);
    }

    if (msg.type === 'clearPenalties') {
      clearAllPenalties();
    }

    // Allow messages from teacher even when taking an exam
    if (msg.msg && msg.sender === 'Your Teacher') {
      studentAppModalState().addStackableMessageFromTeacher(msg.msg);
    }

    if (!getIsTest()) {
      // System messages suppressed while taking an exam.
      // For example, we don't want "New exam available" to pop up.
      if (msg.msg && (!msg.sender || msg.sender !== 'Your Teacher')) {
        gmmAlert({
          msg: msg.msg,
          top: 'Message',
          // So that "Your teacher has been notified..." pops when
          // later message "Your teacher has denied..." arrives.
          removeWhenAnotherDialogShows: true,
        });
      }

      // Teacher can only force hand DOWN
      if (isBoolean(msg.hand)) bannerState().setIsHandRaised(msg.hand);

      if (msg.override) postOverride(problemState().selectedID);

      if (msg.test) {
        const exam = msg.test;

        studentAppModalState().setExamLoading(false);

        if (exam.problems === 'empty') {
          gmmAlert(alerts.emptyExam);
        } else {
          studentAppModalState().closeAll();
          getGmm()?.setToBlank();
          workState().setCurrentWork({
            type: 'EXAM',
            name: exam.testName,
            sitId: msg.sitId,
            testId: msg.testId,
            isFollowUp: exam.isFollowUp,
          } as Exam);

          setProblems(exam, true);
        }
      }
    }

    const messageType = getMessageType(msg);

    if (messageType) {
      renderTeacherResponses(msg, messageType);
    }

    if (getIsTest() && msg.msg && msg.tchrmsg) {
      gmmAlert({
        msg: msg.msg,
        top: 'Message',
      });
    }
  });
};

function renderTeacherResponses(
  msg: MessageTypes,
  messageType: NonNullable<MessageTypes['type'] | MessageTypes['messageType']>
): void {
  switch (messageType) {
    case 'practiceLockChanged': {
      if (applyPracticeLockChangedMessage(msg.manualPracticeLocked)) {
        loginInternal();
      }

      break;
    }

    case 'unlock': {
      unlockAll();
      break;
    }

    case 'denyunlock': {
      const alert = alerts.requestDenied;

      if (allExamProblemsAreLocked()) {
        alert.reload = true;
        alert.reloadIsInternal = true;
      }

      gmmAlert(alert);
      break;
    }

    case 'targetedOverride': {
      const targetedProblem = problemState().problems[`${msg.id}`];

      if (!targetedProblem) {
        logHistory(
          `Ignoring targeted override in renderTeacherResponses, as Restore ${msg.id} is no longer present in colored squares.`
        );

        return;
      }

      // double equals: inbound number vs. client side string-based problem ids
      if (msg.id == bannerState().handRaisedProblemId) {
        changeHandStatus(false);
      }

      const problem: ReceiveProblem = {
        problem: msg.p,
        id: targetedProblem.id,
        performingTargetedOverride: true,
      };

      // If there is a pending replacement problem, the student is currently
      // viewing the answer to a problem on the 'Show Answer' dialog.
      // The targeted override should be delayed until the student closes dialog.
      // Also, the inbound targeted override should become the replacement problem,
      // since the inbound problem is now the official state for the Restore
      // on the server.
      const pendingReplacementProblem = studentAppModalState()
        .replacementProblem;

      // == because one id is a string and the other is a number
      if (
        pendingReplacementProblem &&
        pendingReplacementProblem.id == targetedProblem.id
      ) {
        studentAppModalState().setReplacementProblem(problem);

        return;
      }

      problemState().resetSquare(targetedProblem.id);
      receiveProblem(problem);

      if (studentAppModalState().currentModal !== 'showAnswer') {
        gmmAlert(alerts.targetedOverride);
      }

      break;
    }

    // May no longer be used -- see messageCheck/responseHandler examMessages function
    case 'teacherTurnedInExam': {
      if (!getIsTest()) return;

      submitTest(true).then(() => {
        gmmAlert(alerts.turnedInExam);
      });

      break;
    }

    // May no longer be used -- see messageCheck/responseHandler examMessages function
    case 'teacherPausedExam': {
      if (!getIsTest()) return;

      submitTest().then(() => {
        gmmAlert(alerts.pausedExam);
      });

      break;
    }

    case 'limitMultipleChoice': {
      if (!getIsTest()) {
        gmmAlert(alerts.settingsChanged);
      }

      break;
    }
  }
}

const getMessageType = (
  msg: MessageTypes
): MessageTypes['type'] | MessageTypes['messageType'] => {
  if (msg.type && msg.messageType && msg.type !== msg.messageType) {
    return undefined;
  }

  return msg.type ?? msg.messageType;
};
